import type { LengthAwarePaginator } from "~/types/LengthAwarePaginator";
import type { ApiResponse } from "~/types/ApiResponse";
import { apiRoute, useUniqueId } from "#build/imports";

export interface MediaLibraryQuery {
    search: string;
    collection: string | null;
    per_page: number;
    page: number;
    sorts:
        | "id"
        | "created_at"
        | "updated_at"
        | "-id"
        | "-created_at"
        | "-updated_at";
}

export interface Media {
    id: string;
    collection: string | null;
    name: string;
    file_name: string;
    mime_type: string;
    size: number;
    human_readable_size: string;
    order_column: number;
    manipulations: any[];
    custom_properties: any[];
    generated_conversions: any[];
    responsive_images: any[];
    metadata: {
        alt_text: string;
    };
    relative_url: string;
    full_url: string;
    preview_url: string;
    api_url: string;
    created_at: string;
    updated_at: string;
}

export interface MediaLibraryConfig {
    wait: number;
}

export interface MediaLibraryFileUpload {
    id: string;
    asset: File;
    name: string | null;
    collection: string;
    mode: "image" | "pdf" | "sheet" | "document" | "all" | null;
    file_name: string | null;
    preserve_original: boolean | null;
    progress: number;
    status: "pending" | "uploading" | "finished" | "error";
    last_modified: number;
    blob: string;
    type: string;
    size: number;
    error_message?: string;

    remove(): void;

    attempt(): Promise<void>;
}

export const useMediaLibrary = defineStore("media-library", () => {
    const api = useApi();

    const current = ref<"home" | "unsplash" | "custom">("home");

    const config = ref<MediaLibraryConfig>({
        wait: 250,
    });

    const query = ref<MediaLibraryQuery>({
        search: "",
        collection: null,
        page: 1,
        per_page: 25,
        sorts: "-updated_at",
    });

    const {
        open: openFileDialog,
        reset: clearFiles,
        onChange: onFilesChanged,
    } = useFileDialog({
        accept: "image/*",
    });

    const {
        data,
        pending: searching,
        error,
        refresh,
    } = useAsyncData<ApiResponse<LengthAwarePaginator<Media>>>(
        "media-library",
        () => {
            const routes = {
                home: apiRoute("api.media.index", "media"),
                unsplash: apiRoute(
                    "api.unsplash.search",
                    "third-party/unsplash/search",
                ),
            };

            const currentRoute: string = current.value ?? "home";

            if (currentRoute in routes) {
                return api.request(routes[currentRoute], {
                    params: query.value,
                });
            }

            return { data: { data: [] } };
        },
        {
            immediate: false,
        },
    );

    const refreshDebounced = computed(() =>
        useDebounce(() => refresh(), config.value.wait),
    );

    watch(
        [
            () => query.value.page,
            () => query.value.sorts,
            () => query.value.per_page,
            () => query.value.collection,
            current,
        ],
        () => {
            refresh();
        },
    );

    watch(
        () => query.value.search,
        () => refreshDebounced.value(),
    );

    const assets = computed({
        get: () => (data.value?.data?.data ?? []) as Media[],
        set: (new_assets: Media[]) => {
            data.value = {
                ...data.value,
                data: {
                    ...data.value?.data,
                    data: new_assets,
                },
            };
        },
    });

    const info = computed(() => ({
        current_page: data?.value?.data?.current_page ?? 1,
        from: data?.value?.data?.from ?? 0,
        to: data?.value?.data?.to ?? 0,
        total: data?.value?.data?.total ?? 0,
    }));

    const uploads = ref<MediaLibraryFileUpload[]>([]);

    function createUpload(
        file: File,
        options: Partial<
            Pick<
                MediaLibraryFileUpload,
                "name" | "mode" | "collection" | "preserve_original"
            >
        > = {},
    ) {
        current.value = "home";

        const upload = {
            id: useUniqueId("upload:"),
            file,
            asset: file,
            name: file.name,
            mode: "image",
            file_name: file.name,
            collection: "default",
            preserve_original: null,
            progress: 0,
            status: "pending",
            last_modified: file.lastModified,
            blob: URL.createObjectURL(file),
            type: file.type,
            size: file.size,
            error_message: undefined,
            ...options,
            remove() {
                uploads.value = uploads.value.filter(
                    (upl) => upl.id !== this.id,
                );
            },
            async attempt() {
                const index = uploads.value.findIndex(
                    (upl) => upload.id === upl.id,
                );

                this.progress = uploads.value[index].progress = 0;
                this.status = uploads.value[index].status = "uploading";
                this.error_message = uploads.value[index].error_message =
                    undefined;

                return api
                    .request<ApiResponse<Media>>(
                        apiRoute("api.media.store", "media"),
                        {
                            method: "POST",
                            body: jsonToFormData(this),
                        },
                    )
                    .then((response) => {
                        const current_assets = assets.value;

                        if (current_assets.length === query.value.per_page)
                            current_assets.pop();

                        assets.value = [response.data, ...current_assets];

                        const index = uploads.value.findIndex(
                            (upl) => upload.id === upl.id,
                        );

                        if (index >= 0) {
                            this.progress = uploads.value[index].progress = 100;
                            this.status = uploads.value[index].status =
                                "finished";

                            setTimeout(() => {
                                uploads.value = uploads.value.filter(
                                    (upl) => upl.id !== this.id,
                                );
                            }, 1500);
                        }
                    })
                    .catch((err) => {
                        const index = uploads.value.findIndex(
                            (upl) => upload.id === upl.id,
                        );

                        if (index >= 0) {
                            this.progress = 100;
                            uploads.value[index].status = "error";
                            let message = "Upload Failed";
                            if (err?.response?._data?.message) {
                                message = err.response._data.message;
                            } else if (err?.message) {
                                message = err.message;
                            }
                            uploads.value[index].error_message = message;
                        }
                    });
            },
        };

        return upload as MediaLibraryFileUpload;
    }

    function handleFileUploads(files: FileList | null) {
        if (files) {
            let new_uploads: MediaLibraryFileUpload[] = [];
            for (let file of files) {
                new_uploads.push(createUpload(file));
            }

            uploads.value = [...uploads.value, ...new_uploads];
            new_uploads.forEach((upload) => upload.attempt());
        }
    }

    const updating = ref<boolean>(false);

    function updateMedia(media: Partial<Media>) {
        updating.value = true;
        current.value = "home";

        api.request(
            apiRoute("api.media.update", "media/{id}", {
                id: media.id,
            }),
            {
                method: "PATCH",
                body: media,
            },
        ).finally(() =>
            setTimeout(() => {
                updating.value = false;
            }, 250),
        );
    }

    onFilesChanged(handleFileUploads);

    return {
        // Asset Search
        query,
        assets,
        info,
        current,
        searching,
        handleFileUploads,
        refresh,
        error,

        // File Dialog
        uploads,
        openFileDialog,
        clearFiles,
        onFilesChanged,

        // Asset updates
        updating,
        updateMedia,
    };
});
